/**
 * Validates a Unix cron expression (5-field format: minute hour day month weekday)
 * Returns { valid: true } or { valid: false, error: string }
 */
export interface CronValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validates a 5-field Unix cron expression
 * Format: minute hour day month weekday
 *
 * @param expression - The cron expression to validate
 * @returns CronValidationResult with valid flag and optional error message
 *
 * @example
 * validateCronExpression('0 * * * *') // { valid: true }
 * validateCronExpression('0 0 * * *') // { valid: true }
 * validateCronExpression('invalid')   // { valid: false, error: '...' }
 */
export function validateCronExpression(
  expression: string,
): CronValidationResult {
  if (!expression || !expression.trim()) {
    return { valid: false, error: 'Cron expression cannot be empty' };
  }

  const trimmed = expression.trim();
  const parts = trimmed.split(/\s+/);

  // Must have exactly 5 fields
  if (parts.length !== 5) {
    return {
      valid: false,
      error: `Must have exactly 5 fields (minute hour day month weekday), got ${parts.length}`,
    };
  }

  const [minute, hour, day, month, weekday] = parts;

  // Validate each field
  const minuteValid = validateCronField(minute, 0, 59, 'minute');
  if (!minuteValid.valid) return minuteValid;

  const hourValid = validateCronField(hour, 0, 23, 'hour');
  if (!hourValid.valid) return hourValid;

  const dayValid = validateCronField(day, 1, 31, 'day');
  if (!dayValid.valid) return dayValid;

  const monthValid = validateCronField(month, 1, 12, 'month');
  if (!monthValid.valid) return monthValid;

  // Cron accepts both 0 and 7 for Sunday; our builder emits 7 (Mon=1…Sun=7).
  const weekdayValid = validateCronField(weekday, 0, 7, 'weekday');
  if (!weekdayValid.valid) return weekdayValid;

  return { valid: true };
}

/**
 * Validates a single cron field (supports *, numbers, ranges, steps, lists)
 */
function validateCronField(
  field: string,
  min: number,
  max: number,
  fieldName: string,
): CronValidationResult {
  // Wildcard is always valid
  if (field === '*') {
    return { valid: true };
  }

  // Handle step values: */5, 0-23/2, etc.
  if (field.includes('/')) {
    const parts = field.split('/');
    if (parts.length !== 2) {
      return {
        valid: false,
        error: `Invalid step format in ${fieldName}: ${field}`,
      };
    }
    const [range, step] = parts;
    if (!step || isNaN(Number(step)) || Number(step) <= 0) {
      return {
        valid: false,
        error: `Invalid step value in ${fieldName}: ${step}`,
      };
    }

    // Validate the range part (could be *, a number, or a range)
    if (range !== '*') {
      const rangeValid = validateCronField(range, min, max, fieldName);
      if (!rangeValid.valid) return rangeValid;
    }

    return { valid: true };
  }

  // Handle lists: 1,15,30
  if (field.includes(',')) {
    const items = field.split(',');
    for (const item of items) {
      const itemValid = validateCronField(item.trim(), min, max, fieldName);
      if (!itemValid.valid) return itemValid;
    }
    return { valid: true };
  }

  // Handle ranges: 1-5
  if (field.includes('-')) {
    const parts = field.split('-');
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      return {
        valid: false,
        error: `Invalid range format in ${fieldName}: ${field}`,
      };
    }
    const [start, end] = parts.map((s) => s.trim());
    const startNum = Number(start);
    const endNum = Number(end);

    if (isNaN(startNum) || isNaN(endNum)) {
      return {
        valid: false,
        error: `Invalid range in ${fieldName}: ${field}`,
      };
    }

    if (startNum < min || startNum > max) {
      return {
        valid: false,
        error: `${fieldName} range start ${startNum} out of bounds (${min}-${max})`,
      };
    }

    if (endNum < min || endNum > max) {
      return {
        valid: false,
        error: `${fieldName} range end ${endNum} out of bounds (${min}-${max})`,
      };
    }

    if (startNum > endNum) {
      return {
        valid: false,
        error: `${fieldName} range start ${startNum} > end ${endNum}`,
      };
    }

    return { valid: true };
  }

  // Handle single number
  const num = Number(field);
  if (isNaN(num)) {
    return {
      valid: false,
      error: `Invalid value in ${fieldName}: ${field}`,
    };
  }

  if (num < min || num > max) {
    return {
      valid: false,
      error: `${fieldName} value ${num} out of bounds (${min}-${max})`,
    };
  }

  return { valid: true };
}

/**
 * Common cron expression examples for reference
 */
export const CRON_EXAMPLES = {
  hourly: '0 * * * *',
  daily: '0 0 * * *',
  weekly: '0 0 * * 0',
  monthly: '0 0 1 * *',
  everyFifteenMinutes: '*/15 * * * *',
  weekdaysAt9am: '0 9 * * 1-5',
} as const;
